import {
  Ga4Integration,
  GoogleAdsIntegration,
  MerchantCenterIntegration,
  SearchConsoleIntegration,
  SECRET_FIELD_PATHS,
  SlackIntegration,
  SmtpIntegration,
  TwilioIntegration,
} from '../settings/types';
import { isIntegrationSecretConfigured } from './secretConfigured';
import { apiFetch, useBackendAuth } from './apiClient';

type TestResult = { ok: true; label: string } | { ok: false; message: string };

export async function testSmtpConnection(
  smtp: SmtpIntegration,
  isSecretConfigured: (path: string) => boolean = () => false
): Promise<TestResult> {
  const passwordOk = isIntegrationSecretConfigured(
    isSecretConfigured,
    SECRET_FIELD_PATHS.smtpPassword,
    smtp.password
  );
  if (!smtp.username.trim() || !passwordOk) {
    return { ok: false, message: 'SMTP username and app password are required — click Save after pasting' };
  }
  if (useBackendAuth) {
    try {
      const r = await apiFetch<{ ok: boolean; label: string }>('/api/settings/smtp/test', { method: 'POST' });
      return { ok: true, label: r.label };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : 'SMTP test failed' };
    }
  }
  return {
    ok: true,
    label: `SMTP fields saved for ${smtp.host}:${smtp.port} — enable backend for live Gmail test`,
  };
}

export async function testSearchConsoleConnection(
  sc: SearchConsoleIntegration,
  isSecretConfigured: (path: string) => boolean = () => false
): Promise<TestResult> {
  if (!sc.siteUrl.trim()) {
    return { ok: false, message: 'Site URL is required (e.g. sc-domain:example.com or https://example.com/)' };
  }
  const saOk = isIntegrationSecretConfigured(
    isSecretConfigured,
    SECRET_FIELD_PATHS.searchConsoleSa,
    sc.serviceAccountJson
  );
  if (!saOk) {
    return { ok: false, message: 'Service account JSON is required — click Save after pasting' };
  }
  if (sc.serviceAccountJson.trim()) {
    try {
      JSON.parse(sc.serviceAccountJson);
    } catch {
      return { ok: false, message: 'Service account JSON is not valid JSON' };
    }
  }
  return { ok: true, label: 'Search Console configured — API calls run server-side' };
}

export async function testGa4Connection(
  ga4: Ga4Integration,
  isSecretConfigured: (path: string) => boolean = () => false
): Promise<TestResult> {
  if (!ga4.propertyId.trim()) {
    return { ok: false, message: 'GA4 property ID is required' };
  }
  const saOk = isIntegrationSecretConfigured(
    isSecretConfigured,
    SECRET_FIELD_PATHS.ga4Sa,
    ga4.serviceAccountJson
  );
  if (!saOk) {
    return { ok: false, message: 'Service account JSON is required — click Save after pasting' };
  }
  if (ga4.serviceAccountJson.trim()) {
    try {
      JSON.parse(ga4.serviceAccountJson);
    } catch {
      return { ok: false, message: 'Service account JSON is not valid JSON' };
    }
  }
  return { ok: true, label: 'GA4 configured — reporting runs server-side with the service account' };
}

export async function testMerchantCenterConnection(
  mc: MerchantCenterIntegration,
  isSecretConfigured: (path: string) => boolean = () => false
): Promise<TestResult> {
  if (!mc.merchantId.trim()) {
    return { ok: false, message: 'Merchant Center ID is required' };
  }
  const saOk = isIntegrationSecretConfigured(
    isSecretConfigured,
    SECRET_FIELD_PATHS.merchantCenterSa,
    mc.serviceAccountJson
  );
  if (!saOk) {
    return { ok: false, message: 'Service account JSON is required — click Save after pasting' };
  }
  if (mc.serviceAccountJson.trim()) {
    try {
      JSON.parse(mc.serviceAccountJson);
    } catch {
      return { ok: false, message: 'Service account JSON is not valid JSON' };
    }
  }
  return { ok: true, label: 'Merchant Center configured — product feeds via server API' };
}

export async function testSlackConnection(
  slack: SlackIntegration,
  isSecretConfigured: (path: string) => boolean = () => false
): Promise<TestResult> {
  const tokenOk = isIntegrationSecretConfigured(
    isSecretConfigured,
    SECRET_FIELD_PATHS.slackBotToken,
    slack.botToken
  );
  if (!tokenOk) {
    return { ok: false, message: 'Bot token (xoxb-…) is required — click Save after pasting' };
  }
  return { ok: true, label: 'Slack bot token saved — auth.test runs server-side in production' };
}

export async function testTwilioConnection(
  twilio: TwilioIntegration,
  isSecretConfigured: (path: string) => boolean = () => false
): Promise<TestResult> {
  const tokenOk = isIntegrationSecretConfigured(
    isSecretConfigured,
    SECRET_FIELD_PATHS.twilioAuthToken,
    twilio.authToken
  );
  if (!twilio.accountSid.trim() || !tokenOk) {
    return { ok: false, message: 'Account SID and Auth Token are required — click Save after pasting' };
  }
  return { ok: true, label: 'Twilio credentials saved — balance lookup runs server-side in production' };
}

export async function testGoogleAdsConnection(integration: GoogleAdsIntegration): Promise<TestResult> {
  const { developerToken, clientId, customerId } = integration;
  if (!developerToken.trim() || !clientId.trim() || !customerId.trim()) {
    return { ok: false, message: 'Developer token, OAuth client ID, and Customer ID are required' };
  }
  return {
    ok: true,
    label: 'Google Ads fields saved — API queries need a server-side OAuth refresh token',
  };
}
